import React from 'react';
import PropTypes from 'prop-types';
import { useSelected, useFocused } from 'slate-react';
import { Transforms } from 'slate';
import produce from 'immer';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

export const MENTION_TYPE = 'mention';

const withMentions = editor => {
	const { isInline, isVoid } = editor;

	editor.isInline = element => {
		return element.type === MENTION_TYPE ? true : isInline(element);
	};

	editor.isVoid = element => {
		return element.type === MENTION_TYPE ? true : isVoid(element);
	};

	return editor;
};

const insertMention = (editor, user) => {
	const mention = { type: MENTION_TYPE, user, children: [{ text: '' }] };
	Transforms.insertNodes(editor, mention);
	Transforms.move(editor);
};

const initialState = {
	search: '',
	index: 0,
	target: null,
	suggestions: []
};

const MentionActions = {
	GO_UP: 'GO_UP',
	GO_DOWN: 'GO_DOWN',
	CLEAR_TARGET: 'CLEAR_TARGET',
	UPDATE_SEARCH: 'UPDATE_SEARCH',
	UPDATE_SUGGESTIONS: 'UPDATE_SUGGESTIONS'
};

const mentionReducer = (state, action) => {
	const { suggestions, index } = state;
	switch (action.type) {
		case MentionActions.GO_DOWN:
			return produce(state, draftState => {
				draftState.index = index >= suggestions.length - 1 ? 0 : index + 1;
			});
		case MentionActions.GO_UP:
			return produce(state, draftState => {
				draftState.index = index <= 0 ? suggestions.length - 1 : index - 1;
			});
		case MentionActions.CLEAR_TARGET: {
			return produce(state, draftState => {
				draftState.target = null;
			});
		}
		case MentionActions.UPDATE_SEARCH: {
			return produce(state, draftState => {
				draftState.index = 0;
				draftState.search = action.payload.search;
				draftState.target = action.payload.target;
			});
		}
		case MentionActions.UPDATE_SUGGESTIONS: {
			return produce(state, draftState => {
				draftState.suggestions = action.payload;
			});
		}
		default:
			throw new Error("what's going on?");
	}
};

export const MentionReducer = {
	Actions: MentionActions,
	reducer: mentionReducer,
	initialState
};

export const MENTIONS_CHECK_REGEX = /^@(\w+)$/;

const useStyles = makeStyles(theme => ({
	mention: {
		padding: theme.spacing(0.5),
		margin: '0 1px',
		verticalAlign: 'baseline',
		display: 'inline-block',
		borderRadius: theme.spacing(0.5),
		backgroundColor: '#eee',
		boxShadow: 'none'
	},
	selected: {
		boxShadow: '0 0 0 2px #B4D5FF'
	}
}));

const MentionElement = ({ attributes = {}, children, element }) => {
	const classes = useStyles();
	const selected = useSelected();
	const focused = useFocused();
	return (
		<span
			{...attributes}
			contentEditable
			className={clsx(classes.mention, {
				[classes.selected]: selected && focused
			})}
		>
			@{element.user.name}
			{children}
		</span>
	);
};

MentionElement.propTypes = {
	children: PropTypes.element,
	element: PropTypes.shape({
		user: PropTypes.shape({
			id: PropTypes.number,
			name: PropTypes.string
		})
	}),
	attributes: PropTypes.object
};

export { MentionElement, withMentions, insertMention };
